/**
 * Pure presentation logic for the front page: greeting, category labels,
 * icon choice, description clean-up, search ranking and grouping.
 * Kept free of React so it is cheap to unit-test (model.test.ts).
 */
import type { Form } from '../types'

// ---- greeting ----

export type DayPart = 'morning' | 'afternoon' | 'evening'

export function dayPart(hour: number): DayPart {
    if (hour >= 5 && hour < 12) return 'morning'
    if (hour >= 12 && hour < 17) return 'afternoon'
    return 'evening'
}

/** First name from a display name; handles "Last, First" and email-ish values. */
export function firstName(name: string | undefined | null): string {
    const n = (name ?? '').trim()
    if (!n) return ''
    if (n.includes(',')) {
        const after = n.split(',')[1]?.trim()
        if (after) return after.split(/\s+/)[0]
    }
    const base = n.includes('@') ? n.split('@')[0].split(/[._-]/)[0] : n.split(/\s+/)[0]
    return base ? base.charAt(0).toUpperCase() + base.slice(1) : ''
}

export function greeting(name: string | undefined | null, hour: number): string {
    const fn = firstName(name)
    const base = `Good ${dayPart(hour)}`
    return fn ? `${base}, ${fn}` : base
}

// ---- categories ----

const CATEGORY_LABELS: Record<string, string> = {
    'ai-risk': 'AI risk',
    ai: 'AI risk',
    coi: 'Conflicts of interest',
    ethics: 'Ethics',
    privacy: 'Privacy',
    security: 'Security',
}

/** Categories listed first, in this order; everything else follows alphabetically. */
const CATEGORY_ORDER = ['ai-risk', 'ai', 'coi']

export function titleCase(s: string): string {
    return s
        .replace(/[-_]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ')
}

export function categoryLabel(category: string | undefined | null): string {
    const c = (category ?? '').trim()
    if (!c) return 'General'
    return CATEGORY_LABELS[c.toLowerCase()] ?? titleCase(c)
}

export function compareCategories(a: string, b: string): number {
    const ia = CATEGORY_ORDER.indexOf(a.toLowerCase())
    const ib = CATEGORY_ORDER.indexOf(b.toLowerCase())
    if (ia !== ib) return (ia < 0 ? Infinity : ia) - (ib < 0 ? Infinity : ib) || 0
    return categoryLabel(a).localeCompare(categoryLabel(b))
}

export interface CategoryGroup {
    category: string
    label: string
    forms: Form[]
}

/** Group forms by category (stable, ordered), forms sorted by name within each. */
export function groupByCategory(forms: Form[]): CategoryGroup[] {
    const m = new Map<string, Form[]>()
    for (const f of forms) {
        const key = f.category || ''
        m.set(key, [...(m.get(key) ?? []), f])
    }
    return [...m.entries()]
        .sort(([a], [b]) => compareCategories(a, b))
        .map(([category, list]) => ({
            category,
            label: categoryLabel(category),
            forms: [...list].sort((x, y) => x.name.localeCompare(y.name)),
        }))
}

// ---- icons ----

/** The icons a form author can choose from (kept in sync with the backend's forms.Icons). */
export const ICON_KEYS = [
    'sparkles', 'gift', 'party', 'calendar', 'landmark', 'board', 'coins',
    'heart', 'briefcase', 'users', 'message', 'compass', 'scale', 'shield', 'file',
] as const
export type IconKey = (typeof ICON_KEYS)[number]

export const ICON_LABELS: Record<IconKey, string> = {
    sparkles: 'Sparkles', gift: 'Gift', party: 'Celebration', calendar: 'Calendar', landmark: 'Government',
    board: 'Board', coins: 'Finance', heart: 'Charity', briefcase: 'Business', users: 'People',
    message: 'Messaging', compass: 'Compass', scale: 'Scales', shield: 'Shield', file: 'Document',
}

const isIconKey = (k: string): k is IconKey => (ICON_KEYS as readonly string[]).includes(k)

/** Keyword rules checked in order against the form name (then slug). */
const ICON_RULES: [RegExp, IconKey][] = [
    [/\b(ai|genai|artificial|machine learning|model)\b/i, 'sparkles'],
    [/gift/i, 'gift'],
    [/entertain|hospitality/i, 'party'],
    [/event|conference|sponsor/i, 'calendar'],
    [/government|official|public/i, 'landmark'],
    [/board/i, 'board'],
    [/financ|invest|shares|stock/i, 'coins'],
    [/charit|donat/i, 'heart'],
    [/employ|business/i, 'briefcase'],
    [/relationship|family/i, 'users'],
    [/messag|chat|whatsapp|signal/i, 'message'],
    [/outside|activit/i, 'compass'],
]

const CATEGORY_ICONS: Record<string, IconKey> = { 'ai-risk': 'sparkles', ai: 'sparkles', coi: 'scale' }

/** The icon the author chose, else one guessed from the name. */
export function iconFor(form: Pick<Form, 'name' | 'slug' | 'category'> & { icon?: string }): IconKey {
    if (form.icon && isIconKey(form.icon)) return form.icon
    for (const [re, key] of ICON_RULES) {
        if (re.test(form.name) || re.test(form.slug.replace(/[-_]/g, ' '))) return key
    }
    return CATEGORY_ICONS[(form.category || '').toLowerCase()] ?? 'file'
}

export function categoryIcon(category: string): IconKey {
    return CATEGORY_ICONS[category.toLowerCase()] ?? 'shield'
}

// ---- descriptions ----

const BOILERPLATE = /^select this category to submit (?:an?|the)\s+["“]?[^"”]*["”]?\s+request\.?$/i

/**
 * The form's own description, with the imported boilerplate ("Select this
 * category to submit an "X" request.") removed. Empty when there is nothing
 * real to show: descriptions are never invented.
 */
export function cleanDescription(form: Pick<Form, 'description'>): string {
    const d = (form.description ?? '').replace(/\s+/g, ' ').trim()
    if (!d || BOILERPLATE.test(d)) return ''
    return d.replace(/^select this category (?=if|to|when)/i, 'Use this ')
}

// ---- search ----

const norm = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
const words = (s: string) => norm(s).split(/[^a-z0-9]+/).filter(Boolean)

/**
 * Score a form against a query; 0 means no match. Every query token must
 * match somewhere. Name matches outrank category, which outrank description.
 */
export function scoreForm(form: Form, query: string): number {
    const tokens = words(query)
    if (tokens.length === 0) return 1
    const name = norm(form.name)
    const nameWords = words(form.name)
    const cat = norm(`${categoryLabel(form.category)} ${form.category} ${form.number_prefix}`)
    const desc = norm(`${form.description} ${cleanDescription(form)}`)
    let total = 0
    for (const t of tokens) {
        let s = 0
        if (nameWords.some((w) => w === t)) s = 40
        else if (nameWords.some((w) => w.startsWith(t))) s = 30
        else if (name.includes(t)) s = 20
        else if (cat.includes(t)) s = 10
        else if (desc.includes(t)) s = 5
        if (s === 0) return 0
        total += s
    }
    if (name.startsWith(norm(query.trim()))) total += 25
    return total
}

/** Forms matching the query, best first (ties: most used, then name). */
export function searchForms(forms: Form[], query: string): Form[] {
    if (!query.trim()) return forms
    return forms
        .map((f) => ({ f, s: scoreForm(f, query) }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s || b.f.submission_count - a.f.submission_count || a.f.name.localeCompare(b.f.name))
        .map((x) => x.f)
}

/** Up to `n` most-used forms (only those that have actually been used). */
export function featuredForms(forms: Form[], n = 3): Form[] {
    return forms
        .filter((f) => f.submission_count > 0)
        .sort((a, b) => b.submission_count - a.submission_count || a.name.localeCompare(b.name))
        .slice(0, n)
}

export function resultsAnnouncement(count: number, query: string): string {
    if (!query.trim()) return ''
    if (count === 0) return `No forms match “${query.trim()}”.`
    return `${count} form${count === 1 ? '' : 's'} match. Press Enter to start the first one.`
}
